import React, { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Table,
  Button,
  Tag,
  Space,
  Input,
  Popconfirm,
  Tooltip,
  Badge,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  PoweroffOutlined,
  GroupOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import axios from "axios";
import PageTitle from "../page-header/PageHeader";

const ItemGroupList = () => {
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("item-group?status=true");
      setGroups(data?.data || []);
    } catch {
      toast.error("Failed to load item groups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleToggle = async (id) => {
    try {
      await axios.patch(`item-group/${id}`);
      fetchGroups();
      toast.success("Group status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const filtered = groups.filter((g) =>
    g.name?.toLowerCase().includes(search.toLowerCase()) ||
    g.customer?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      title: "Group Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Link to={`/item-group/${record.id}/edit`}>
          <Space>
            <GroupOutlined />
            <strong>{name}</strong>
          </Space>
        </Link>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, record) => record.customer?.name || "—",
    },
    {
      title: "Parent Item",
      key: "parent",
      render: (_, record) => {
        const parent = Array.isArray(record.members)
          ? record.members.find((m) => m.is_parent)
          : null;
        return parent ? (
          <Tag color="blue">{parent.name}</Tag>
        ) : "—";
      },
    },
    {
      title: "Members",
      key: "members",
      render: (_, record) => {
        const members = Array.isArray(record.members) ? record.members : [];
        return (
          <Space wrap>
            {members.map((m, i) => (
              <Tooltip
                key={i}
                title={`${m.qty_per_group} × ${m.name}`}
              >
                <Tag color={m.is_parent ? "gold" : "default"}>
                  {m.qty_per_group}× {m.name.length > 20 ? m.name.slice(0, 20) + "…" : m.name}
                  {m.is_parent ? " (parent)" : ""}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Pricing Preview",
      key: "pricing",
      render: (_, record) => {
        const members = Array.isArray(record.members) ? record.members : [];
        const parent = members.find((m) => m.is_parent);
        const groupTotal = members.reduce((sum, m) => {
          const rs = m.running_size;
          const rate = rs?.last_sale_price || 0;
          return sum + rate * m.qty_per_group;
        }, 0);
        if (!parent || groupTotal === 0) return "—";
        const parentWise = groupTotal / parent.qty_per_group;
        return (
          <Space direction="vertical" size={0}>
            <span style={{ fontSize: 12, color: "#888" }}>Group total: ₹{groupTotal.toFixed(2)}</span>
            <strong style={{ color: "#1677ff" }}>
              {parent.name.slice(0, 18)}…-wise: ₹{parentWise.toFixed(2)}
            </strong>
          </Space>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active) => (
        <Badge status={active ? "success" : "error"} text={active ? "Active" : "Inactive"} />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Link to={`/item-group/${record.id}/edit`}>
            <Button size="small" icon={<EditOutlined />}>Edit</Button>
          </Link>
          <Popconfirm
            title={record.active ? "Deactivate this group?" : "Activate this group?"}
            onConfirm={() => handleToggle(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              size="small"
              icon={<PoweroffOutlined />}
              danger={record.active}
            >
              {record.active ? "Deactivate" : "Activate"}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!isLogged) return <Navigate to="/auth/login" replace />;

  return (
    <>
      <PageTitle title="Back" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Item Groups</h2>
        <Space>
          <Input.Search
            placeholder="Search by name or customer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
          <Link to="/item-group/new">
            <Button type="primary" icon={<PlusOutlined />}>New Group</Button>
          </Link>
        </Space>
      </div>

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="middle"
      />
    </>
  );
};

export default ItemGroupList;
